/**
 * ImpersonationBanner — F8 T8.5 (czerwony pasek na gorze panelu klienta gdy sesja
 * jest impersonacja).
 *
 * Komponent prezentacyjny — przyjmuje wszystko via props, bez sięgania do contextu
 * (parent w `TenantLayout` dostaje dane z `useTenantAuth().me` i przekazuje).
 *
 * Pokazuje:
 *  - Email administratora ITE ktory aktualnie podszywa sie pod uzytkownika.
 *  - Licznik czasu pozostalego do wygasniecia tokenu (gdy expiresAt podany).
 *  - Przycisk "Zakoncz" wywolujacy `onEnd`. Gdy onEnd rzuci (backend nie potwierdzil
 *    revoke) → pokaz error toast i zostaw banner zamontowany. Defense in depth:
 *    bez tego admin widzi zamknieta karte ale JTI lezy aktywny do 30min.
 *
 * Patrz ADR-0013 (impersonacja: lifecycle + UX baner).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ImpersonationBannerProps {
  /** Czy sesja jest impersonacja. Gdy false -> komponent nic nie renderuje. */
  isImpersonation: boolean;
  /** Email pracownika ITE wykonujacego impersonacje. */
  adminEmail: string | null;
  /** ISO string momentu wygasniecia tokenu impersonacji (gdy null -> brak licznika). */
  expiresAt: string | null;
  /** Handler klikniecia "Zakoncz" — typowo POST /auth/impersonate/end. */
  onEnd: () => void | Promise<void>;
}

function formatRemaining(
  expiresAt: string,
  now: number,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const remaining = new Date(expiresAt).getTime() - now;
  if (remaining <= 0) return t('tenant.impersonationBanner.expired');
  const minutes = Math.ceil(remaining / 60_000);
  return t('tenant.impersonationBanner.minutesShort', { count: minutes });
}

export function ImpersonationBanner({
  isImpersonation,
  adminEmail,
  expiresAt,
  onEnd,
}: ImpersonationBannerProps) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  const [ending, setEnding] = useState(false);
  const [endError, setEndError] = useState<string | null>(null);

  useEffect(() => {
    if (!isImpersonation || !expiresAt) return;
    const interval = setInterval(() => setNow(Date.now()), 30_000); // refresh co 30s
    return () => clearInterval(interval);
  }, [isImpersonation, expiresAt]);

  if (!isImpersonation) return null;

  const handleEnd = async (): Promise<void> => {
    setEndError(null);
    setEnding(true);
    try {
      await onEnd();
    } catch {
      // Backend nie potwierdzil revoke (network/500). Trzymamy banner zamontowany +
      // pokazujemy error zeby admin mogl ponowic. Bez tego JTI lezy aktywny do 30min
      // mimo ze admin widzi "zakonczone".
      setEndError(t('tenant.impersonationBanner.endError'));
      setEnding(false);
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-50 flex w-full flex-col gap-1 bg-red-600 px-4 py-2 text-sm text-white"
      role="alert"
    >
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{t('tenant.impersonationBanner.title')}</span>
          <span>
            {t('tenant.impersonationBanner.by')} <strong>{adminEmail ?? '?'}</strong>
          </span>
          {expiresAt && (
            <span data-testid="impersonation-countdown" className="opacity-90">
              {t('tenant.impersonationBanner.remaining', {
                value: formatRemaining(expiresAt, now, t),
              })}
            </span>
          )}
          <span className="opacity-80">{t('tenant.impersonationBanner.logged')}</span>
        </div>
        <button
          type="button"
          onClick={() => {
            void handleEnd();
          }}
          disabled={ending}
          className="rounded border border-white/40 bg-white/10 px-3 py-1 font-medium hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {ending ? t('tenant.impersonationBanner.ending') : t('tenant.impersonationBanner.end')}
        </button>
      </div>
      {endError && (
        <div
          data-testid="impersonation-end-error"
          role="status"
          className="text-xs font-medium text-yellow-100"
        >
          {endError}
        </div>
      )}
    </div>
  );
}
